// Aggressions: a political action against a weaker rival (strictly weaker; never someone as strong or stronger).
//   Playing one spends its military actions (twice as many against Gandhi) whatever happens, and the card is discarded.
//   The defender may then defend (always asked, even when they could not succeed, so as not to give away their hand):
//   with up to as many military cards as they have military actions (not spent), each Defense card worth its defense
//   value and any other military card worth 1. All go to the discard; Defense cards are shown, the others not. If their
//   strength plus that reaches the aggressor's, the aggression fails (and the aggressor gets nothing).
//   Otherwise it succeeds and the aggressor makes its choices (which buildings, which card, ...).
// The aggression under way is kept in the game state:
//   { card, aggressor, defender, cost, aggressorStrength, defenderStrength, statsBefore }
const CardConverter = require('../Utilities/Converters/CardConverter');
const CardToData = require('../Utilities/Converters/CardToData');
const { MilitaryCardTypes, CivilCardTypes } = require('../Constants/Names/CardTypes');
const AggressionNames = require('../Constants/Names/AggressionNames');
const { AgeOrder } = require('../Constants/Constants');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { Rules, HasRuleException, FireTriggers, TriggerEvents, LogTrigger, Situations, SituationalStrength } = require('../Effects/Effects');
const BonusCard = require('../entities/Cards/BonusCard');
const { RequestDecision, builtCards } = require('./Decisions');
const { GetAllStats, PlayerChanges } = require('../Utilities/Stats');
const { CardRef, HiddenRef } = require('../Utilities/CardRefs');
const WonderNames = require('../Constants/Names/WonderNames');

const UrbanTypes = Object.values(TechnologyTypes.Urban);
const Level = (card) => Math.max(0, AgeOrder.indexOf(card.age));

// ---------------- Himeji Castle: one unit's strength added to a side, then sacrificed

const HasHimeji = (player) => player.wonders.some(wonder => wonder.name === WonderNames.HimejiCastle && !wonder.crumbled);
// The units the player could add: their built military units, with each one's strength
const HimejiUnits = (player) => HasHimeji(player)
    ? player.technologies.filter(tech => Object.values(TechnologyTypes.Military).includes(tech.type) && tech.GetWorkerCount() > 0)
        .map(tech => ({ name: tech.name, strength: tech.multiplier }))
    : [];
const HimejiStrength = (player, name) => HimejiUnits(player).find(unit => unit.name === name)?.strength ?? 0;
const HimejiProblem = (player, name) => name === undefined || name === null ? null
    : HimejiUnits(player).some(unit => unit.name === name) ? null : "Himeji Castle adds one of your units";
// The units added, sacrificed once the aggression or war is evaluated: { playerNumber: unit name }
const SacrificeHimeji = (gameState, units) => Object.entries(units ?? {}).filter(([, name]) => name).map(([number, name]) => {
    const player = gameState.playerStates[number];
    player.GetTechnology(name)?.AddContent("workers", -1);
    return `${player.name}'s ${CardRef({ type: 'Technology', name, age: player.GetTechnology(name)?.age })} is sacrificed (Himeji Castle)`;
});

// ---------------- Costs and who can be attacked

// Military actions an aggression or war costs against this rival: twice as many, or 3 more, against one protected so
// (Gandhi: the base game's, the expansion's)
const ProtectedCost = (cost, defender) => HasRuleException(defender, Rules.DoubleAggressionCost) ? cost * 2
    : HasRuleException(defender, Rules.ExtraAggressionCost) ? cost + 3 : cost;
const AggressionCost = (card, defender) => ProtectedCost(card.aggression.cost, defender);

// The aggressor's strength against this rival, with pact bonuses for attacking them (Open Borders Agreement)
const AttackStrength = (aggressor, defender) =>
    aggressor.GetMilitaryStrength() + require('./Pact').AttackBonus(aggressor.gameState, aggressor, defender);

// The defender's strength against an aggression, with what cards add then (Nostradamus)
const DefenseStrength = (defender) => SituationalStrength(defender, Situations.DefendAggression);

// Why the aggression cannot be played against this rival now, or null (himejiUnit: a unit Himeji Castle adds to the
// aggressor's strength)
const AggressionProblem = (aggressor, defender, card, himejiUnit) => {
    if (HasRuleException(aggressor, Rules.NoAggression))
    {
        return "Your leader does not allow aggressions";
    }
    if (!defender || defender === aggressor || defender.resigned)
    {
        return "Choose a rival";
    }
    const pact = require('./Pact').AttackProblem(aggressor.gameState, aggressor, defender);
    if (pact)
    {
        return pact;
    }
    const himeji = HimejiProblem(aggressor, himejiUnit);
    if (himeji)
    {
        return himeji;
    }
    if (AttackStrength(aggressor, defender) + HimejiStrength(aggressor, himejiUnit) <= DefenseStrength(defender))
    {
        return `${defender.name} is as strong as you or stronger`;
    }
    const cost = AggressionCost(card, defender);
    return aggressor.GetMilitaryActionsRemaining() < cost ? `Needs ${cost} military action${cost === 1 ? '' : 's'}` : null;
};

// ---------------- What each aggression does when it succeeds
// options(gameState, aggressor, defender, card): the aggressor's choices (listed answers), or null when there is
// nothing to choose; apply(gameState, aggressor, defender, card, value): what it does, as log text.
// shortfall(aggressor, defender, card): why the full benefit cannot be had from this rival, for the target picker.
const takeUpTo = (have, want) => Math.max(0, Math.min(have, want));

// Raid: the urban buildings it can destroy: Age I one of age I or older (A, I); Age II one of age II or older and one
// of age I or older; Age III one of age III or older and one of age II or older
const RaidSlots = { 'Age I': [1], 'Age II': [2, 1], 'Age III': [3, 2] };
const RaidOptions = (defender, card) => {
    const slots = RaidSlots[card.age] ?? [1];
    const buildings = builtCards(defender, UrbanTypes);
    const fits = (tech, slot) => Level(tech) <= slot;
    const worth = (techs) => Math.ceil(techs.reduce((sum, tech) => sum + tech.workerCost, 0) / 2);
    const options = new Map();
    const add = (techs) => {
        const key = techs.map(tech => tech.name).sort().join('+');
        if (!options.has(key))
        {
            options.set(key, { label: `${techs.map(tech => tech.name).join(' and ')} (${worth(techs)} resources)`, value: techs.map(tech => tech.name) });
        }
    };
    buildings.filter(tech => slots.some(slot => fits(tech, slot))).forEach(tech => add([tech]));
    if (slots.length > 1)
    {
        buildings.filter(a => fits(a, slots[0])).forEach(a => buildings.filter(b => fits(b, slots[1])).forEach(b => {
            if (a !== b || a.GetWorkerCount() >= 2)
            {
                add([a, b]);
            }
        }));
    }
    // The biggest raids first
    return [...options.values()].sort((x, y) => y.value.length - x.value.length);
};

// Kidnap: the civil cards the aggressor could take from the rival's hand, by the usual card-taking limits
const KidnapOptions = (aggressor, defender) => {
    if (aggressor.civilHand.length >= aggressor.GetCivilHandLimit())
    {
        return [];
    }
    return defender.civilHand.filter(card => {
        if (card.type === CivilCardTypes.Technology
            && (aggressor.HasTechnology(card.name) || aggressor.civilHand.some(c => c.name === card.name)))
        {
            return false;
        }
        return !(card.type === CivilCardTypes.Leader && aggressor.leaderAgesTaken.includes(card.age));
    }).map(card => ({ label: `${card.name} (${card.age})`, value: card.id }));
};

const Splits = (total, food, resources) => Array.from({ length: total + 1 }, (_, f) => ({ food: f, resources: total - f }))
    .filter(split => split.food <= food && split.resources <= resources)
    .map(split => ({
        label: [split.food ? `${split.food} food` : null, split.resources ? `${split.resources} resources` : null].filter(p => p).join(', ') || 'nothing',
        value: split,
    }));

const Plunder = (card) => require('../Constants/Aggressions/Plunder').GetResourceAmount(card.age);

// statsOnly: what it does is just numbers changing hands, which the recap shows (its text is left out there)
const Effects = {
    [AggressionNames.Enslave]: {
        statsOnly: true,
        apply: (gameState, aggressor, defender) => {
            aggressor.GainFood(2);
            aggressor.GainResources(2);
            // The loser loses a population as usual: an unused worker, or else one of their choice (and the turn waits)
            if (defender.workerPool > 0)
            {
                defender.workerPool--;
            } else {
                RequestDecision(gameState, {
                    player: defender, type: 'loseWorker', event: AggressionNames.Enslave, blocking: true,
                    prompt: "You have no unused worker: choose the card to take a worker from",
                    options: builtCards(defender).map(tech => ({ label: tech.name, value: tech.name })),
                });
            }
            return `${defender.name} loses 1 population; ${aggressor.name} gains 2 food and 2 resources`;
        },
        shortfall: (aggressor, defender) => builtCards(defender).length === 0 && defender.workerPool === 0 ? `${defender.name} has no population to lose` : null,
    },
    [AggressionNames.Plunder]: {
        statsOnly: true,
        options: (gameState, aggressor, defender, card) => {
            const total = takeUpTo(defender.GetFood() + defender.GetResources(), Plunder(card));
            return Splits(total, defender.GetFood(), defender.GetResources());
        },
        apply: (gameState, aggressor, defender, card, value) => {
            defender.LoseFood(value.food);
            defender.LoseResources(value.resources);
            aggressor.GainFood(value.food);
            aggressor.GainResources(value.resources);
            return `${aggressor.name} takes ${value.food} food and ${value.resources} resources from ${defender.name}`;
        },
        shortfall: (aggressor, defender, card) => defender.GetFood() + defender.GetResources() < Plunder(card)
            ? `${defender.name} has only ${defender.GetFood() + defender.GetResources()} food and resources` : null,
    },
    [AggressionNames.Raid]: {
        options: (gameState, aggressor, defender, card) => RaidOptions(defender, card),
        // Picked on the defender's board: as many buildings as the card's age allows
        boardPick: (card) => (RaidSlots[card.age] ?? [1]).length,
        apply: (gameState, aggressor, defender, card, value) => {
            const destroyed = value.map(name => defender.GetTechnology(name));
            destroyed.forEach(tech => { tech.AddContent("workers", -1); defender.workerPool++; });
            const gain = Math.ceil(destroyed.reduce((sum, tech) => sum + tech.workerCost, 0) / 2);
            aggressor.GainResources(gain);
            return `${aggressor.name} destroys ${defender.name}'s ${destroyed.map(tech => CardRef({ type: 'Technology', name: tech.name, age: tech.age })).join(' and ')} and gains ${gain} resources`;
        },
        shortfall: (aggressor, defender, card) => {
            const options = RaidOptions(defender, card);
            const most = Math.max(0, ...options.map(o => o.value.length));
            return most < (RaidSlots[card.age] ?? [1]).length ? (most === 0 ? `${defender.name} has no building it can destroy` : `Only 1 building can be destroyed`) : null;
        },
    },
    [AggressionNames.Kidnap]: {
        // (the aggressor may take no card)
        options: (gameState, aggressor, defender) => [...KidnapOptions(aggressor, defender), { label: "Take no card", value: null }],
        apply: (gameState, aggressor, defender, card, value) => {
            const taken = defender.civilHand.find(c => c.id === value);
            if (!taken)
            {
                return `${aggressor.name} takes no card from ${defender.name}'s hand`;
            }
            defender.civilHand = defender.civilHand.filter(c => c.id !== value);
            aggressor.civilHand.push(taken);
            if (taken.type === CivilCardTypes.Leader)
            {
                aggressor.leaderAgesTaken.push(taken.age);
            }
            aggressor.turnFlags.push(`kidnapped:${taken.id}`);
            return `${aggressor.name} takes ${taken.name} from ${defender.name}'s hand (3 culture if played this turn)`;
        },
        shortfall: (aggressor, defender) => KidnapOptions(aggressor, defender).length === 0 ? `No card in ${defender.name}'s hand can be taken` : null,
    },
    [AggressionNames.Infiltrate]: {
        options: (gameState, aggressor, defender) => [
            ...(defender.leader ? [{ label: `${defender.leader.name} (leader, ${3 * Level(defender.leader)} culture)`, value: 'leader' }] : []),
            ...(defender.wonderInProgress ? [{ label: `${defender.wonderInProgress.card.name} (unfinished wonder, ${3 * Level(defender.wonderInProgress.card)} culture)`, value: 'wonder' }] : []),
        ],
        apply: (gameState, aggressor, defender, card, value) => {
            const removed = value === 'leader' ? defender.leader : defender.wonderInProgress.card;
            if (value === 'leader')
            {
                FireTriggers(defender, TriggerEvents.LeaderLeaves, { leader: defender.leader });
                defender.leader = null;
            } else {
                defender.wonderInProgress = null;
            }
            const culture = 3 * Level(removed);
            aggressor.culture += culture;
            return `${aggressor.name} removes ${defender.name}'s ${removed.name} and scores ${culture} culture`;
        },
        shortfall: (aggressor, defender) => !defender.leader && !defender.wonderInProgress ? `${defender.name} has no leader or unfinished wonder` : null,
    },
    [AggressionNames.Annex]: {
        options: (gameState, aggressor, defender) => defender.colonies.map(colony => ({ label: colony.name, value: colony.id })),
        apply: (gameState, aggressor, defender, card, value) => {
            const colony = defender.colonies.find(c => c.id === value);
            defender.colonies = defender.colonies.filter(c => c.id !== value);
            // Only what it gives while held changes hands; nothing happens at once
            aggressor.colonies.push(colony);
            return `${aggressor.name} annexes ${defender.name}'s ${CardRef(colony)}`;
        },
        shortfall: (aggressor, defender) => defender.colonies.length === 0 ? `${defender.name} has no colonies` : null,
    },
    [AggressionNames.Spy]: {
        statsOnly: true,
        apply: (gameState, aggressor, defender) => {
            const science = takeUpTo(defender.science, 5);
            defender.science -= science;
            aggressor.science += science;
            return `${aggressor.name} takes ${science} science from ${defender.name}`;
        },
        shortfall: (aggressor, defender) => defender.science < 5 ? `${defender.name} has only ${defender.science} science` : null,
    },
    [AggressionNames.ArmedIntervention]: {
        statsOnly: true,
        apply: (gameState, aggressor, defender) => {
            const culture = takeUpTo(defender.culture, 7);
            defender.culture -= culture;
            aggressor.culture += culture;
            return `${aggressor.name} takes ${culture} culture from ${defender.name}`;
        },
        shortfall: (aggressor, defender) => defender.culture < 7 ? `${defender.name} has only ${defender.culture} culture` : null,
    },
    [AggressionNames.Occupy]: {
        statsOnly: true,
        apply: (gameState, aggressor, defender) => {
            const resources = takeUpTo(defender.GetResources(), 3);
            const food = takeUpTo(defender.GetFood(), 3);
            const science = takeUpTo(defender.science, 3);
            const culture = takeUpTo(defender.culture, 3);
            defender.LoseResources(resources); aggressor.GainResources(resources);
            defender.LoseFood(food); aggressor.GainFood(food);
            defender.science -= science; aggressor.science += science;
            defender.culture -= culture; aggressor.culture += culture;
            return `${aggressor.name} takes ${resources} resources, ${food} food, ${science} science and ${culture} culture from ${defender.name}`;
        },
        shortfall: (aggressor, defender) => {
            const short = [['resources', defender.GetResources()], ['food', defender.GetFood()], ['science', defender.science], ['culture', defender.culture]]
                .filter(([, have]) => have < 3).map(([what, have]) => `${have} ${what}`);
            return short.length ? `${defender.name} has only ${short.join(', ')}` : null;
        },
    },
};

// ---------------- The flow

const AggressionCard = (gameState) => CardConverter(gameState.aggression.card);

// For the target picker: what the aggressor would face against each rival
const TargetInfo = (gameState, aggressor, card) => gameState.playerStates.filter(p => p !== aggressor && !p.resigned).map(defender => ({
    player: defender.playerNumber,
    name: defender.name,
    strength: DefenseStrength(defender),
    // Himeji Castle: the most a unit could add to their defense (its strength + 3), or 0
    himejiDefense: Math.max(0, ...HimejiUnits(defender).map(unit => unit.strength + 3)),
    // What cards add to it only when defending (e.g. Nostradamus +3): [{ source, value }]
    defenseExtras: defender.situationalStrengthDelegate.filter(d => d.situation === Situations.DefendAggression)
        .map(d => ({ source: d.Source?.name, value: d.Action() })).filter(extra => extra.value !== 0),
    militaryActions: defender.GetMilitaryActionCount(),
    hand: defender.militaryHand.map(c => c.age),
    cost: AggressionCost(card, defender),
    baseCost: card.aggression.cost,
    problem: AggressionProblem(aggressor, defender, card),
    shortfall: Effects[card.name]?.shortfall?.(aggressor, defender, card) ?? null,
    // Pacts it would end, and strength added for attacking this rival
    breaks: require('./Pact').EndedByAttack(gameState, aggressor, defender)
        .map(pact => `Ends your ${pact.card.name} with ${defender.name}`),
    attackBonus: require('./Pact').AttackBonus(gameState, aggressor, defender),
}));

// The aggression is under way: the defender is asked whether to defend
// statsBefore: everyone's stats before it was played (the card still in hand, the actions not yet spent), so the
// recap counts them
const StartAggression = (gameState, aggressor, defender, card, statsBefore = GetAllStats(gameState), himejiUnit = null) => {
    // (Himeji Castle: a unit's strength added, 3 culture; the unit sacrificed when it is over)
    const himeji = HimejiStrength(aggressor, himejiUnit);
    if (himejiUnit && himeji >= 0)
    {
        aggressor.culture += 3;
        LogTrigger(aggressor, WonderNames.HimejiCastle, `${himejiUnit} adds ${himeji} strength (sacrificed after), +3 culture`);
    }
    const aggressorStrength = AttackStrength(aggressor, defender) + (himejiUnit ? himeji : 0);
    const defenderStrength = DefenseStrength(defender);
    gameState.aggression = {
        card: CardToData(card), aggressor: aggressor.playerNumber, defender: defender.playerNumber,
        aggressorStrength, defenderStrength, statsBefore,
        // (units Himeji Castle adds, sacrificed when it is over)
        himeji: himejiUnit ? { [aggressor.playerNumber]: himejiUnit } : {},
    };
    RequestDecision(gameState, {
        player: defender, type: 'defend', event: card.name, blocking: true, ui: 'defend', options: null,
        prompt: `${aggressor.name} plays ${card.name} against you: strength ${aggressorStrength} against your ${defenderStrength}. Defend?`,
        // (and for the defend box's line: who plays what, both strengths)
        context: { needed: aggressorStrength - defenderStrength, limit: defender.GetMilitaryActionCount(),
            aggressor: aggressor.playerNumber, card: CardToData(card), aggressorStrength, defenderStrength,
            // (Himeji Castle: a unit the defender may add, its strength + 3)
            himejiUnits: HimejiUnits(defender) },
    });
};

// A defense: the military cards used (an array of ids), or { cards, himejiUnit } with a unit Himeji Castle adds
const DefenseParts = (value) => Array.isArray(value) ? { cards: value, himejiUnit: null } : { cards: value?.cards ?? [], himejiUnit: value?.himejiUnit ?? null };

// What defending with these military cards is worth: Defense cards their value, any other 1; and a unit Himeji Castle
// adds, its strength + 3
const DefenseValue = (defender, value) => {
    const { cards, himejiUnit } = DefenseParts(value);
    return defender.militaryHand.filter(c => cards.includes(c.id))
        .reduce((sum, c) => sum + (c.type === MilitaryCardTypes.Bonus ? BonusCard.GetDefenseBonus(c.age) : 1), 0)
        + (himejiUnit ? HimejiStrength(defender, himejiUnit) + 3 : 0);
};

// needed: the strength short of the aggressor's; a defense either matches it or plays nothing (a rival who cannot or will
// not stop the aggression gives up no cards)
const DefenseProblem = (defender, value, limit, needed) => {
    const { cards: cardIds, himejiUnit } = DefenseParts(value);
    const himeji = HimejiProblem(defender, himejiUnit);
    if (himeji)
    {
        return himeji;
    }
    if (!Array.isArray(cardIds) || new Set(cardIds).size !== cardIds.length || cardIds.some(id => !defender.militaryHand.some(c => c.id === id)))
    {
        return "Choose military cards from your hand";
    }
    if (cardIds.length > limit)
    {
        return `You can use at most ${limit} cards (your military actions)`;
    }
    const defending = cardIds.length > 0 || himejiUnit;
    return defending && needed !== undefined && DefenseValue(defender, value) < needed
        ? `That is ${DefenseValue(defender, value)}, not enough to stop it (${needed} needed): defend with enough, or not at all` : null;
};

// The defender's answer: the cards go to the discard; then the aggression fails, or succeeds and does its work
const Defend = (gameState, decision, answer) => {
    const aggression = gameState.aggression;
    const aggressor = gameState.playerStates[aggression.aggressor];
    const defender = gameState.playerStates[aggression.defender];
    const card = AggressionCard(gameState);
    const { cards: cardIds, himejiUnit } = DefenseParts(answer);
    const value = DefenseValue(defender, answer);
    if (himejiUnit)
    {
        aggression.himeji = { ...(aggression.himeji ?? {}), [defender.playerNumber]: himejiUnit };
    }
    const used = defender.militaryHand.filter(c => cardIds.includes(c.id));
    defender.militaryHand = defender.militaryHand.filter(c => !cardIds.includes(c.id));
    gameState.militaryDiscard.push(...used);
    // Defense cards are shown; any other card used is not
    const shown = used.filter(c => c.type === MilitaryCardTypes.Bonus);
    // (as the cards: the Defense cards, then the others face down)
    const parts = [...shown.map(CardRef), ...used.filter(c => !shown.includes(c)).map(c => HiddenRef(c.age)),
        ...(himejiUnit ? [`${CardRef({ type: 'Technology', name: himejiUnit, age: defender.GetTechnology(himejiUnit)?.age })} (Himeji Castle)`] : [])];
    const usedText = parts.length === 0 ? "does not defend" : `defends with ${parts.join(' + ')} (+${value})`;
    if (aggression.defenderStrength + value >= aggression.aggressorStrength)
    {
        Finish(gameState, [`${defender.name} ${usedText}`]);
        return `${usedText}: ${CardRef(card)} fails`;
    }
    const effect = Effects[card.name];
    if (effect?.options)
    {
        // Succeeded: now the aggressor chooses (for everyone's status line)
        aggression.stage = 'choose';
        RequestDecision(gameState, {
            player: aggressor, type: 'aggressionChoice', event: card.name, blocking: true,
            prompt: effect.boardPick ? `${CardRef(card)} succeeds: pick on ${defender.name}'s board what it destroys` : `${CardRef(card)} succeeds: choose what it does`,
            options: effect.options(gameState, aggressor, defender, card),
            // (picked on the defender's board, as many as it allows: see GameEngine.GetBoardSelection)
            ...(effect.boardPick ? { ui: 'boardPick', context: { target: defender.playerNumber, pickLabel: 'Destroy', pickCount: effect.boardPick(card) } } : {}),
        });
        // Nothing to choose from: no effect
        if (gameState.aggression && !gameState.decisions.some(d => d.type === 'aggressionChoice'))
        {
            Finish(gameState, [`${defender.name} concedes defeat`, `${CardRef(card)} has no effect`]);
        }
    } else {
        const text = effect ? effect.apply(gameState, aggressor, defender, card) : null;
        Finish(gameState, [`${defender.name} concedes defeat`, ...(text && !effect.statsOnly ? [text] : [])]);
    }
    return `${usedText}: ${CardRef(card)} succeeds`;
};

// The aggressor's choice when it succeeded
const Choose = (gameState, value) => {
    const aggression = gameState.aggression;
    const card = AggressionCard(gameState);
    const defender = gameState.playerStates[aggression.defender];
    const effect = Effects[card.name];
    const text = effect.apply(gameState, gameState.playerStates[aggression.aggressor], defender, card, value);
    Finish(gameState, [`${defender.name} concedes defeat`, ...(effect.statsOnly ? [] : [text])]);
    return text;
};

// The aggression is over: the card is discarded, and the outcome goes into everyone's "since your last turn", a line each
// under the aggression: the pacts it ended, whether it was defended, then what it did beyond the numbers
const Finish = (gameState, lines) => {
    const aggression = gameState.aggression;
    const card = AggressionCard(gameState);
    // Units Himeji Castle added are sacrificed, now it has been evaluated
    const sacrificed = SacrificeHimeji(gameState, aggression.himeji);
    lines = [...(aggression.lines ?? []), ...lines, ...sacrificed];
    gameState.militaryDiscard.push(card);
    const aggressor = gameState.playerStates[aggression.aggressor];
    gameState.reports.push({
        name: card.name, age: card.age, card: CardToData(card), revealedBy: aggressor.name, round: gameState.round,
        // (both strengths as it was played, so the defense's math can be checked: "5 strength vs 3 strength")
        target: gameState.playerStates[aggression.defender].name, text: `${aggression.aggressorStrength} strength vs ${aggression.defenderStrength} strength`, lines,
        changes: PlayerChanges(aggression.statsBefore, GetAllStats(gameState)),
    });
    gameState.aggression = null;
};

// Kidnap: a card taken with it and played the same turn scores 3 culture
const KidnapBonus = (player, card) => {
    const flag = `kidnapped:${card.id}`;
    if (player.turnFlags.includes(flag))
    {
        player.turnFlags = player.turnFlags.filter(f => f !== flag);
        player.culture += 3;
        LogTrigger(player, AggressionNames.Kidnap, "+3 culture for playing the kidnapped card this turn");
    }
};

module.exports = {
    HasHimeji,
    HimejiUnits,
    HimejiStrength,
    SacrificeHimeji,
    DefenseParts,
    AggressionCost,
    ProtectedCost,
    AggressionProblem,
    TargetInfo,
    StartAggression,
    DefenseValue,
    DefenseProblem,
    Defend,
    Choose,
    KidnapBonus,
    AggressionCard,
    Finish,
    AggressionEffects: Effects,
};
